/**
 * Image uploads to Cloudinary: signed uploads only.
 *
 *   uploadImage(file, { purpose: "team-logo", teamId })
 *   uploadImage(file, { purpose: "registration-logo" })
 *
 * The browser names a purpose (plus the team id for team logos) and nothing
 * else. This client's Supabase Edge Function `cloudinary-sign` checks the
 * signed-in user, chooses the folder, file name and allowed file formats under
 * the client's server-side root, and returns a SHA-256 signature for exactly
 * those fields. The browser then posts the file with those fields, unchanged,
 * straight to Cloudinary. There is no unsigned upload path and no client-side
 * folder, preset or cloud name: if the signer is unavailable, uploads fail.
 */

import { supabase } from "./supabaseClient";

/** File types the server accepts for logos (keep the file pickers in line). */
export const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp";

const PURPOSES = ["team-logo", "registration-logo"];
const UPLOAD_URL = /^https:\/\/api\.cloudinary\.com\/v1_1\/[a-z0-9_-]+\/image\/upload$/i;

const MESSAGES = {
  denied: "No se pudo autorizar la subida de la imagen.",
  invalid: "La autorización de subida no es válida.",
  format: "Formato no permitido. Usa PNG, JPG o WebP.",
};

async function callSigner(body) {
  const { data, error } = await supabase.functions.invoke("cloudinary-sign", { body });
  if (error) {
    let message = "";
    try {
      message = (await error.context.json()).error || "";
    } catch {
      /* not a JSON error body */
    }
    throw new Error(message || MESSAGES.denied);
  }
  return data;
}

/** Refuse anything that is not a complete server-signed upload. */
function checkAuthorization(auth) {
  const fields = auth?.fields || {};
  const ok =
    UPLOAD_URL.test(String(auth?.upload_url || "")) &&
    /^[0-9a-f]{64}$/.test(String(fields.signature || "")) &&
    Boolean(fields.allowed_formats) &&
    Boolean(fields.folder) &&
    !("upload_preset" in fields);
  if (!ok) throw new Error(MESSAGES.invalid);
}

async function uploadSigned(file, auth) {
  checkAuthorization(auth);
  const formData = new FormData();
  for (const [key, value] of Object.entries(auth.fields)) formData.append(key, value);
  formData.append("file", file, typeof File !== "undefined" && file instanceof File ? file.name : "logo.png");
  const res = await fetch(auth.upload_url, { method: "POST", body: formData });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.secure_url) {
    const msg = body.error?.message || "";
    throw new Error(/format/i.test(msg) ? MESSAGES.format : msg || `Error al subir imagen (${res.status})`);
  }
  return { url: body.secure_url, publicId: body.public_id };
}

/**
 * Upload an image for a purpose.
 * @param {File|Blob} file
 * @param {{ purpose: "team-logo"|"registration-logo", teamId?: string }} opts
 */
export async function uploadImage(file, { purpose, ...ids } = {}) {
  if (!PURPOSES.includes(purpose)) {
    throw new Error(`Tipo de imagen desconocido: "${purpose}".`);
  }
  return uploadSigned(file, await callSigner({ purpose, ...ids }));
}
